import { X } from 'lucide-react';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center gap-1 pl-2 pr-1 py-1 h-7 box-border rounded-md border transition-all select-none group text-xs font-medium',
  {
    variants: {
      intent: {
        destructive:
          'bg-destructive/10 border-destructive/20 text-destructive hover:bg-destructive/20 hover:border-destructive/30',
        default:
          'bg-primary/5 border-primary/20 text-primary hover:bg-primary/10 hover:border-primary/40',
        neutral:
          'bg-muted-foreground/10 border-muted-foreground/20 text-muted-foreground hover:bg-muted-foreground/20 hover:border-muted-foreground/30',
        inclusive:
          'bg-green-500/10 border-green-500/20 text-green-700 hover:bg-green-500/20 hover:border-green-500/30',
      },
    },
    defaultVariants: {
      intent: 'default',
    },
  },
);

// 2. Add 'intent' to the interface
interface SelectionBadgeProps<T> extends VariantProps<typeof badgeVariants> {
  item: T;
  getCode: (item: T) => string;
  getLabel: (item: T) => string;
  onRemove: (item: T) => void;
  className?: string;
  intent?: 'destructive' | 'default' | 'neutral' | 'inclusive';
}

export function SelectionBadge<T>({
  item,
  getCode,
  getLabel,
  onRemove,
  intent, // Destructure intent here
  className,
}: SelectionBadgeProps<T>) {
  return (
    <button
      type="button"
      onClick={() => onRemove(item)}
      // 3. Apply variants here
      className={cn(badgeVariants({ intent }), className)}
      title={`Remove ${getLabel(item)}`}
    >
      {/* Code Pill */}
      <span
        className={cn(
          'font-mono text-[13px]',
          // Dynamically color the text based on intent
          intent === 'destructive'
            ? 'text-destructive'
            : intent === 'neutral'
              ? 'text-muted-foreground'
              : intent === 'inclusive'
                ? 'text-green-700'
                : 'text-primary',
        )}
      >
        {getCode(item)}
      </span>

      {/* Main Label */}
      <span className="text-foreground/80 max-w-[150px] truncate text-[13px]">
        {getLabel(item)}
      </span>

      {/* Remove indicator */}
      <X className="w-3 h-3 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}
